import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

// FontSize extension (TextStyle'ı extend eder)
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize || null,
        renderHTML: attributes => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}` };
        },
      },
    };
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

interface Belge {
  belgeID: number;
  belgeAdi: string;
  olusturmaTarihi: string;
  guncellemeTarihi: string;
  olusturanAdi: string;
  olusturanKullaniciID: number;
  sahip: boolean;
  duzenlemeYetkisi: boolean;
  paylasimSayisi: number;
}

interface BelgeDetay extends Belge {
  icerik: string;
  paylasimlar: {
    paylasimID: number;
    kullaniciID: number;
    kullaniciAdi: string;
    duzenlemeYetkisi: boolean;
    paylasimTarihi: string;
  }[];
}

interface Kullanici {
  kullaniciID: number;
  adSoyad: string;
  email?: string;
}

// ─── Helper ─────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function getUser() {
  try {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

// ─── Toolbar ────────────────────────────────────────────────────────────────

interface ToolbarProps { editor: Editor | null; onSave: () => void; saving: boolean; }

const FONT_FAMILIES = [
  { label: 'Varsayılan', value: '' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Verdana', value: 'Verdana' },
];

const FONT_SIZES = ['8', '10', '11', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '72'];

const TBBtn: React.FC<{
  active?: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, disabled, title, onClick, children }) => (
  <button
    title={title}
    onMouseDown={e => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    style={{
      minWidth: 28, height: 28, padding: '0 5px',
      border: active ? '1px solid #1a2440' : '1px solid transparent',
      borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? 'rgba(26,36,64,0.1)' : 'transparent',
      color: disabled ? '#ccc' : '#374151',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 500,
      transition: 'all 0.1s',
    }}
    onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(26,36,64,0.07)'; }}
    onMouseLeave={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = active ? 'rgba(26,36,64,0.1)' : 'transparent'; }}
  >{children}</button>
);

const TBSep = () => (
  <div style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 4px', flexShrink: 0 }} />
);

const Toolbar: React.FC<ToolbarProps> = ({ editor, onSave, saving }) => {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [highlightPickerOpen, setHighlightPickerOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt('Resim URL girin:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const setLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const COLORS = ['#000000', '#374151', '#dc2626', '#d97706', '#16a34a', '#2563eb', '#7c3aed', '#db2777', '#ffffff', '#f9fafb', '#fef2f2', '#fffbeb', '#ecfdf5', '#eff6ff', '#faf5ff'];
  const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fde68a', '#fecaca', '#e9d5ff', '#fed7aa'];

  return (
    <div style={{
      background: '#fff',
      borderBottom: '1px solid #e5e7eb',
      padding: '6px 12px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 2,
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      {/* Kaydet */}
      <button
        onClick={onSave}
        disabled={saving}
        style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: '#1a2440', color: '#fff', fontSize: 12, fontWeight: 600,
          cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 4,
        }}
      >
        <i className="fas fa-save" style={{ fontSize: 11 }} />
        {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </button>
      <TBSep />

      {/* Font Family */}
      <select
        title="Yazı Tipi"
        value={editor.getAttributes('textStyle').fontFamily || ''}
        onChange={e => {
          if (e.target.value) editor.chain().focus().setFontFamily(e.target.value).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
        style={{ height: 28, fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 4, padding: '0 4px', outline: 'none', maxWidth: 130 }}
      >
        {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>

      {/* Font Size */}
      <select
        title="Yazı Boyutu"
        onChange={e => {
          const size = e.target.value;
          if (size) {
            editor.chain().focus().setMark('textStyle', { fontSize: size + 'pt' }).run();
          }
        }}
        style={{ height: 28, fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 4, padding: '0 4px', outline: 'none', width: 54 }}
        value={editor.getAttributes('textStyle').fontSize?.replace('pt', '') || '12'}
      >
        {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <TBSep />

      {/* Heading */}
      <TBBtn title="Başlık 1" active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</TBBtn>
      <TBBtn title="Başlık 2" active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</TBBtn>
      <TBBtn title="Başlık 3" active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</TBBtn>
      <TBSep />

      {/* Format */}
      <TBBtn title="Kalın (Ctrl+B)" active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></TBBtn>
      <TBBtn title="İtalik (Ctrl+I)" active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></TBBtn>
      <TBBtn title="Altı Çizili (Ctrl+U)" active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></TBBtn>
      <TBBtn title="Üstü Çizili" active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></TBBtn>
      <TBSep />

      {/* Color */}
      <div style={{ position: 'relative' }}>
        <TBBtn title="Yazı Rengi" onClick={() => { setColorPickerOpen(o => !o); setHighlightPickerOpen(false); }}>
          <span style={{ borderBottom: `3px solid ${editor.getAttributes('textStyle').color || '#000'}`, lineHeight: 1.2 }}>A</span>
        </TBBtn>
        {colorPickerOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setColorPickerOpen(false)} />
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', display: 'grid', gridTemplateColumns: 'repeat(5, 24px)', gap: 4 }}>
              {COLORS.map(c => (
                <button key={c} onMouseDown={e => { e.preventDefault(); editor.chain().focus().setColor(c).run(); setColorPickerOpen(false); }}
                  style={{ width: 24, height: 24, background: c, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Highlight */}
      <div style={{ position: 'relative' }}>
        <TBBtn title="Vurgu Rengi" active={editor.isActive('highlight')}
          onClick={() => { setHighlightPickerOpen(o => !o); setColorPickerOpen(false); }}>
          <i className="fas fa-highlighter" style={{ fontSize: 11 }} />
        </TBBtn>
        {highlightPickerOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setHighlightPickerOpen(false)} />
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', display: 'grid', gridTemplateColumns: 'repeat(7, 24px)', gap: 4 }}>
              {HIGHLIGHTS.map(c => (
                <button key={c} onMouseDown={e => { e.preventDefault(); editor.chain().focus().setHighlight({ color: c }).run(); setHighlightPickerOpen(false); }}
                  style={{ width: 24, height: 24, background: c, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }} />
              ))}
              <button onMouseDown={e => { e.preventDefault(); editor.chain().focus().unsetHighlight().run(); setHighlightPickerOpen(false); }}
                style={{ width: 24, height: 24, background: '#fff', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: 10 }}>✕</button>
            </div>
          </>
        )}
      </div>
      <TBSep />

      {/* Align */}
      <TBBtn title="Sola Hizala" active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}>
        <i className="fas fa-align-left" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="Ortala" active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}>
        <i className="fas fa-align-center" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="Sağa Hizala" active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}>
        <i className="fas fa-align-right" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="İki Yana Yayılı" active={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}>
        <i className="fas fa-align-justify" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBSep />

      {/* Lists */}
      <TBBtn title="Madde İşaretli Liste" active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <i className="fas fa-list-ul" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="Numaralı Liste" active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <i className="fas fa-list-ol" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="Alıntı" active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <i className="fas fa-quote-right" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="Kod Bloğu" active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}>
        <i className="fas fa-code" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBSep />

      {/* Indent */}
      <TBBtn title="Girintili Yap" onClick={() => editor.chain().focus().sinkListItem('listItem').run()}>
        <i className="fas fa-indent" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="Girintiyi Kaldır" onClick={() => editor.chain().focus().liftListItem('listItem').run()}>
        <i className="fas fa-outdent" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBSep />

      {/* Table */}
      <TBBtn title="Tablo Ekle" onClick={insertTable}>
        <i className="fas fa-table" style={{ fontSize: 11 }} />
      </TBBtn>
      {editor.isActive('table') && (
        <>
          <TBBtn title="Sütun Ekle (Sağa)" onClick={() => editor.chain().focus().addColumnAfter().run()}>+↓</TBBtn>
          <TBBtn title="Satır Ekle (Alta)" onClick={() => editor.chain().focus().addRowAfter().run()}>+→</TBBtn>
          <TBBtn title="Sütun Sil" onClick={() => editor.chain().focus().deleteColumn().run()}>-↓</TBBtn>
          <TBBtn title="Satır Sil" onClick={() => editor.chain().focus().deleteRow().run()}>-→</TBBtn>
          <TBBtn title="Tabloyu Sil" onClick={() => editor.chain().focus().deleteTable().run()}>
            <i className="fas fa-trash" style={{ fontSize: 10, color: '#dc2626' }} />
          </TBBtn>
        </>
      )}
      <TBSep />

      {/* Image */}
      <TBBtn title="Resim Ekle" onClick={addImage}>
        <i className="fas fa-image" style={{ fontSize: 11 }} />
      </TBBtn>

      {/* Link */}
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <TBBtn title="Bağlantı Ekle" active={editor.isActive('link')}
          onClick={() => { if (editor.isActive('link')) editor.chain().focus().unsetLink().run(); else setShowLinkInput(o => !o); }}>
          <i className="fas fa-link" style={{ fontSize: 11 }} />
        </TBBtn>
        {showLinkInput && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowLinkInput(false)} />
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', display: 'flex', gap: 4, whiteSpace: 'nowrap' }}>
              <input
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://..."
                style={{ width: 200, padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, outline: 'none' }}
                onKeyDown={e => { if (e.key === 'Enter') setLink(); }}
                autoFocus
              />
              <button onMouseDown={e => { e.preventDefault(); setLink(); }}
                style={{ padding: '4px 10px', background: '#1a2440', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Ekle
              </button>
            </div>
          </>
        )}
      </div>
      <TBSep />

      {/* Undo/Redo */}
      <TBBtn title="Geri Al (Ctrl+Z)" disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}>
        <i className="fas fa-undo" style={{ fontSize: 11 }} />
      </TBBtn>
      <TBBtn title="İleri Al (Ctrl+Y)" disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}>
        <i className="fas fa-redo" style={{ fontSize: 11 }} />
      </TBBtn>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const MetinEditoru: React.FC = () => {
  const user = getUser();
  const kullaniciID: number = user?.kullaniciID ?? 0;

  const [belgeler, setBelgeler] = useState<Belge[]>([]);
  const [loading, setLoading] = useState(false);
  const [acikBelge, setAcikBelge] = useState<BelgeDetay | null>(null);
  const [saving, setSaving] = useState(false);

  // Modals
  const [showYeniModal, setShowYeniModal] = useState(false);
  const [yeniBelgeAdi, setYeniBelgeAdi] = useState('');
  const [yeniLoading, setYeniLoading] = useState(false);

  const [showPaylasModal, setShowPaylasModal] = useState(false);
  const [tumKullanicilar, setTumKullanicilar] = useState<Kullanici[]>([]);
  const [paylasAra, setPaylasAra] = useState('');
  const [secilenPaylasimlar, setSecilenPaylasimlar] = useState<{ kullaniciID: number; duzenlemeYetkisi: boolean }[]>([]);
  const [paylasLoading, setPaylasLoading] = useState(false);

  const [showKopyalaModal, setShowKopyalaModal] = useState(false);
  const [kopyaAdi, setKopyaAdi] = useState('');
  const [kopyaBelgeID, setKopyaBelgeID] = useState<number | null>(null);

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [eksporting, setEksporting] = useState(false);

  const editorRef = useRef<HTMLDivElement>(null);

  // Otomatik kayıt için debounce
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);

  // ─── Editor ───────────────────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      FontSize,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image,
      Link.configure({ openOnClick: false }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      if (!acikBelge || !acikBelge.duzenlemeYetkisi) return;
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(() => {
        handleAutoSave(editor.getHTML());
      }, 2000);
    },
    editable: false,
  });

  // ─── Data ─────────────────────────────────────────────────────────────────

  const fetchBelgeler = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/MetinBelgeleri', { params: { kullaniciID } });
      setBelgeler(res.data);
    } catch { toast.error('Belgeler yüklenemedi'); }
    setLoading(false);
  }, [kullaniciID]);

  useEffect(() => { fetchBelgeler(); }, [fetchBelgeler]);

  const openBelge = async (belgeID: number) => {
    try {
      const res = await axiosInstance.get(`/api/MetinBelgeleri/${belgeID}`, { params: { kullaniciID } });
      setAcikBelge(res.data);
      editor?.setEditable(res.data.duzenlemeYetkisi);
      editor?.commands.setContent(res.data.icerik || '');
      // Paylaşım listesini hazırla
      setSecilenPaylasimlar(res.data.paylasimlar.map((p: any) => ({
        kullaniciID: p.kullaniciID,
        duzenlemeYetkisi: p.duzenlemeYetkisi,
      })));
    } catch { toast.error('Belge açılamadı'); }
  };

  const handleAutoSave = async (html: string) => {
    if (!acikBelge) return;
    try {
      await axiosInstance.put(`/api/MetinBelgeleri/${acikBelge.belgeID}`, { icerik: html });
      setAcikBelge(prev => prev ? { ...prev, guncellemeTarihi: new Date().toISOString() } : prev);
    } catch { /* sessiz hata */ }
  };

  const handleSave = async () => {
    if (!acikBelge || !editor) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/MetinBelgeleri/${acikBelge.belgeID}`, {
        belgeAdi: acikBelge.belgeAdi,
        icerik: editor.getHTML(),
      });
      setAcikBelge(prev => prev ? { ...prev, guncellemeTarihi: new Date().toISOString() } : prev);
      toast.success('Belge kaydedildi');
      fetchBelgeler();
    } catch { toast.error('Kaydedilemedi'); }
    setSaving(false);
  };

  const handleYeniBelge = async () => {
    if (!yeniBelgeAdi.trim()) { toast.warn('Belge adı girin'); return; }
    setYeniLoading(true);
    try {
      const res = await axiosInstance.post('/api/MetinBelgeleri', {
        belgeAdi: yeniBelgeAdi.trim(),
        icerik: '',
        olusturanKullaniciID: kullaniciID,
      });
      setShowYeniModal(false);
      setYeniBelgeAdi('');
      await fetchBelgeler();
      await openBelge(res.data.belgeID);
    } catch { toast.error('Belge oluşturulamadı'); }
    setYeniLoading(false);
  };

  const handleDelete = async (belgeID: number, belgeAdi: string) => {
    if (!window.confirm(`"${belgeAdi}" belgesini silmek istediğinize emin misiniz?`)) return;
    try {
      await axiosInstance.delete(`/api/MetinBelgeleri/${belgeID}`, { params: { kullaniciID } });
      toast.success('Belge silindi');
      if (acikBelge?.belgeID === belgeID) {
        setAcikBelge(null);
        editor?.commands.clearContent();
      }
      fetchBelgeler();
    } catch { toast.error('Silinemedi'); }
  };

  const handleKopyala = async () => {
    if (!kopyaBelgeID) return;
    try {
      const res = await axiosInstance.post(`/api/MetinBelgeleri/${kopyaBelgeID}/kopyala`, {
        yeniBelgeAdi: kopyaAdi.trim() || undefined,
        olusturanKullaniciID: kullaniciID,
      });
      toast.success('Belge kopyalandı');
      setShowKopyalaModal(false);
      setKopyaAdi('');
      setKopyaBelgeID(null);
      await fetchBelgeler();
      await openBelge(res.data.belgeID);
    } catch { toast.error('Kopyalanamadı'); }
  };

  // ─── Paylaşım ─────────────────────────────────────────────────────────────

  const openPaylasModal = async () => {
    setPaylasAra('');
    try {
      const res = await axiosInstance.get('/api/Kullanicilar');
      setTumKullanicilar(res.data.filter((k: Kullanici) => k.kullaniciID !== kullaniciID));
    } catch { toast.error('Kullanıcılar yüklenemedi'); }
    setShowPaylasModal(true);
  };

  const togglePaylasim = (k: Kullanici) => {
    setSecilenPaylasimlar(prev => {
      const mevcut = prev.find(p => p.kullaniciID === k.kullaniciID);
      if (mevcut) return prev.filter(p => p.kullaniciID !== k.kullaniciID);
      return [...prev, { kullaniciID: k.kullaniciID, duzenlemeYetkisi: false }];
    });
  };

  const toggleDuzenleme = (kullaniciID: number) => {
    setSecilenPaylasimlar(prev =>
      prev.map(p => p.kullaniciID === kullaniciID ? { ...p, duzenlemeYetkisi: !p.duzenlemeYetkisi } : p)
    );
  };

  const handlePaylas = async () => {
    if (!acikBelge) return;
    setPaylasLoading(true);
    try {
      await axiosInstance.post(`/api/MetinBelgeleri/${acikBelge.belgeID}/paylas`, {
        paylasimlar: secilenPaylasimlar,
      });
      toast.success('Paylaşım güncellendi');
      setShowPaylasModal(false);
      await openBelge(acikBelge.belgeID);
      fetchBelgeler();
    } catch { toast.error('Paylaşım kaydedilemedi'); }
    setPaylasLoading(false);
  };

  // ─── Export ───────────────────────────────────────────────────────────────

  const handlePdfExport = async () => {
    if (!acikBelge || !editorRef.current) return;
    setEksporting(true);
    setShowExportMenu(false);
    try {
      const el = editorRef.current.querySelector('.ProseMirror') as HTMLElement;
      if (!el) { toast.error('İçerik bulunamadı'); return; }
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 15;
      const imgW = pageW - 2 * margin;
      const imgH = (canvas.height * imgW) / canvas.width;
      const pageContentH = pageH - 2 * margin;
      let offset = 0; let page = 0;
      while (offset < imgH) {
        if (page > 0) doc.addPage();
        doc.addImage(imgData, 'PNG', margin, margin - offset, imgW, imgH);
        offset += pageContentH; page++;
      }
      doc.save(`${acikBelge.belgeAdi}.pdf`);
      toast.success('PDF indirildi');
    } catch { toast.error('PDF oluşturulamadı'); }
    setEksporting(false);
  };

  const handleDocxExport = async () => {
    if (!acikBelge || !editor) return;
    setEksporting(true);
    setShowExportMenu(false);
    try {
      // HTML'den basit text extraction
      const html = editor.getHTML();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const docxParagraphs: Paragraph[] = [];
      const processNode = (node: ChildNode): Paragraph | null => {
        const el = node as HTMLElement;
        if (el.tagName === 'H1') return new Paragraph({ text: el.textContent || '', heading: HeadingLevel.HEADING_1 });
        if (el.tagName === 'H2') return new Paragraph({ text: el.textContent || '', heading: HeadingLevel.HEADING_2 });
        if (el.tagName === 'H3') return new Paragraph({ text: el.textContent || '', heading: HeadingLevel.HEADING_3 });
        if (el.tagName === 'P' || el.tagName === 'DIV') {
          const runs: TextRun[] = [];
          el.childNodes.forEach(child => {
            const ce = child as HTMLElement;
            const bold = ce.tagName === 'STRONG' || ce.tagName === 'B';
            const italic = ce.tagName === 'EM' || ce.tagName === 'I';
            const underline = ce.tagName === 'U';
            runs.push(new TextRun({ text: child.textContent || '', bold, italics: italic, underline: underline ? {} : undefined }));
          });
          if (runs.length === 0) runs.push(new TextRun({ text: el.textContent || '' }));
          return new Paragraph({ children: runs });
        }
        if (el.tagName === 'LI') return new Paragraph({ text: '• ' + (el.textContent || ''), indent: { left: 720 } });
        return new Paragraph({ text: el.textContent || '' });
      };

      tempDiv.childNodes.forEach(node => {
        const p = processNode(node);
        if (p) docxParagraphs.push(p);
      });
      if (docxParagraphs.length === 0) docxParagraphs.push(new Paragraph({ text: '' }));

      const docx = new Document({
        sections: [{ properties: {}, children: docxParagraphs }],
      });
      const blob = await Packer.toBlob(docx);
      saveAs(blob, `${acikBelge.belgeAdi}.docx`);
      toast.success('DOCX indirildi');
    } catch { toast.error('DOCX oluşturulamadı'); }
    setEksporting(false);
  };

  const handlePrint = () => {
    if (!acikBelge || !editorRef.current) return;
    setShowExportMenu(false);
    const el = editorRef.current.querySelector('.ProseMirror') as HTMLElement;
    if (!el) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html><html><head>
      <title>${acikBelge.belgeAdi}</title>
      <style>
        @page { size: A4; margin: 20mm; }
        body { font-family: 'Times New Roman', serif; font-size: 12pt; color: #000; }
        h1 { font-size: 20pt; } h2 { font-size: 16pt; } h3 { font-size: 14pt; }
        table { border-collapse: collapse; width: 100%; }
        td, th { border: 1px solid #000; padding: 4px 8px; }
        blockquote { border-left: 3px solid #999; padding-left: 12px; color: #555; }
        code { font-family: 'Courier New', monospace; background: #f0f0f0; padding: 2px 4px; }
        pre { background: #f0f0f0; padding: 12px; }
        ul, ol { padding-left: 24px; }
        img { max-width: 100%; }
      </style>
      </head><body>${el.innerHTML}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const filteredKullanicilar = tumKullanicilar.filter(k =>
    k.adSoyad.toLowerCase().includes(paylasAra.toLowerCase()) ||
    (k.email || '').toLowerCase().includes(paylasAra.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)', fontFamily: "'Inter', -apple-system, sans-serif", background: '#f8f9fa' }}>

      {/* ── Sol Panel: Belge Listesi (col-3) — Belge açıkken gizlenir ── */}
      {!acikBelge && (
      <div style={{ width: '25%', maxWidth: 320, flexShrink: 0, background: '#fff', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '16px 14px 10px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Metin Editörü</h2>
              <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>{belgeler.length} belge</p>
            </div>
            <button
              onClick={() => setShowYeniModal(true)}
              title="Yeni Belge"
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#1a2440', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              <i className="fas fa-plus" style={{ fontSize: 12 }} />
            </button>
          </div>
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>
              <i className="fas fa-spinner fa-spin" />
            </div>
          ) : belgeler.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 13 }}>
              <i className="fas fa-file-alt" style={{ fontSize: 32, display: 'block', marginBottom: 8, color: '#d1d5db' }} />
              Henüz belge yok.<br />
              <button onClick={() => setShowYeniModal(true)}
                style={{ marginTop: 8, color: '#1a2440', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                + Yeni belge oluştur
              </button>
            </div>
          ) : (
            belgeler.map(b => (
              <div
                key={b.belgeID}
                onClick={() => openBelge(b.belgeID)}
                style={{
                  padding: '9px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 2,
                  background: 'transparent',
                  border: '1px solid transparent',
                  transition: 'all 0.1s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f9fafb'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <i className={`fas ${b.sahip ? 'fa-file-alt' : 'fa-share-alt'}`}
                    style={{ fontSize: 13, color: b.sahip ? '#1a2440' : '#cd6906', marginTop: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.belgeAdi}
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                      {!b.sahip && <span style={{ color: '#cd6906', marginRight: 4 }}>{b.olusturanAdi}</span>}
                      {new Date(b.guncellemeTarihi).toLocaleDateString('tr-TR')}
                    </div>
                  </div>
                  {/* Aksiyonlar */}
                  <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button
                      title="Kopyala"
                      onClick={() => { setKopyaBelgeID(b.belgeID); setKopyaAdi(b.belgeAdi + ' (Kopya)'); setShowKopyalaModal(true); }}
                      style={{ width: 22, height: 22, border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#1a2440')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                    ><i className="fas fa-copy" style={{ fontSize: 10 }} /></button>
                    {b.sahip && (
                      <button
                        title="Sil"
                        onClick={() => handleDelete(b.belgeID, b.belgeAdi)}
                        style={{ width: 22, height: 22, border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#dc2626')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                      ><i className="fas fa-trash" style={{ fontSize: 10 }} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      )}

      {/* ── Sağ Panel: Editör ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!acikBelge ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <i className="fas fa-file-alt" style={{ fontSize: 56, marginBottom: 16, color: '#d1d5db' }} />
            <p style={{ fontSize: 15, margin: 0, fontWeight: 500 }}>Bir belge seçin veya yeni oluşturun</p>
            <button onClick={() => setShowYeniModal(true)}
              style={{ marginTop: 12, padding: '8px 20px', background: '#1a2440', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              + Yeni Belge
            </button>
          </div>
        ) : (
          <>
            {/* Belge Header */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              {/* Geri: Belge Listesi */}
              <button
                onClick={() => { setAcikBelge(null); editor?.commands.clearContent(); }}
                title="Belge Listesine Dön"
                style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: '#374151', flexShrink: 0 }}
              >
                <i className="fas fa-arrow-left" style={{ fontSize: 11 }} />
                Belgeler
              </button>
              <input
                value={acikBelge.belgeAdi}
                onChange={e => setAcikBelge(prev => prev ? { ...prev, belgeAdi: e.target.value } : prev)}
                onBlur={() => {
                  if (acikBelge.duzenlemeYetkisi)
                    axiosInstance.put(`/api/MetinBelgeleri/${acikBelge.belgeID}`, { belgeAdi: acikBelge.belgeAdi }).catch(() => { });
                }}
                disabled={!acikBelge.duzenlemeYetkisi}
                style={{ fontSize: 16, fontWeight: 600, color: '#111827', border: 'none', outline: 'none', background: 'transparent', flex: 1, padding: '2px 0' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {formatDate(acikBelge.guncellemeTarihi)}
                </span>
                {!acikBelge.duzenlemeYetkisi && (
                  <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 20, fontWeight: 500 }}>
                    Sadece Okuma
                  </span>
                )}
                {acikBelge.paylasimSayisi > 0 && (
                  <span style={{ fontSize: 11, background: 'rgba(26,36,64,0.08)', color: '#1a2440', padding: '2px 8px', borderRadius: 20, fontWeight: 500 }}>
                    <i className="fas fa-users" style={{ marginRight: 4, fontSize: 10 }} />
                    {acikBelge.paylasimSayisi} kişiyle paylaşıldı
                  </span>
                )}
                {/* Paylaş */}
                {acikBelge.sahip && (
                  <button onClick={openPaylasModal}
                    style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: '#374151' }}>
                    <i className="fas fa-share-alt" style={{ fontSize: 11, color: '#cd6906' }} />
                    Paylaş
                  </button>
                )}
                {/* Dışa Aktar */}
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowExportMenu(m => !m)}
                    style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: '#374151' }}>
                    {eksporting ? <i className="fas fa-spinner fa-spin" style={{ fontSize: 11 }} /> : <i className="fas fa-download" style={{ fontSize: 11 }} />}
                    Dışa Aktar
                  </button>
                  {showExportMenu && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowExportMenu(false)} />
                      <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 160, overflow: 'hidden' }}>
                        {[
                          { icon: 'fa-file-pdf', label: 'PDF İndir', color: '#e67d21', onClick: handlePdfExport },
                          { icon: 'fa-file-word', label: 'DOCX İndir', color: '#2563eb', onClick: handleDocxExport },
                          { icon: 'fa-print', label: 'Yazdır', color: '#374151', onClick: handlePrint },
                        ].map(item => (
                          <button key={item.label} onClick={item.onClick}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151', textAlign: 'left', borderTop: '1px solid #f0f0f0' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                            onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                          >
                            <i className={`fas ${item.icon}`} style={{ color: item.color }} />
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Toolbar */}
            {acikBelge.duzenlemeYetkisi && (
              <Toolbar editor={editor} onSave={handleSave} saving={saving} />
            )}

            {/* A4 Editing Area */}
            <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5', padding: '24px 0' }}>
              <div ref={editorRef}
                style={{
                  width: 794, minHeight: 1123, margin: '0 auto',
                  background: '#fff',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                  padding: '54px 72px',
                  boxSizing: 'border-box',
                }}>
                <style>{`
                  .ProseMirror {
                    outline: none;
                    min-height: 100%;
                    font-size: 12pt;
                    font-family: 'Times New Roman', serif;
                    line-height: 1.6;
                    color: #111;
                  }
                  .ProseMirror h1 { font-size: 22pt; margin: 16px 0 8px; }
                  .ProseMirror h2 { font-size: 17pt; margin: 14px 0 6px; }
                  .ProseMirror h3 { font-size: 14pt; margin: 12px 0 4px; }
                  .ProseMirror p { margin: 0 0 8px; }
                  .ProseMirror ul, .ProseMirror ol { padding-left: 24px; margin: 8px 0; }
                  .ProseMirror li { margin: 2px 0; }
                  .ProseMirror blockquote {
                    border-left: 3px solid #9ca3af;
                    padding-left: 16px;
                    margin: 12px 0;
                    color: #6b7280;
                  }
                  .ProseMirror code {
                    background: #f3f4f6;
                    padding: 2px 5px;
                    border-radius: 4px;
                    font-size: 11pt;
                    font-family: 'Courier New', monospace;
                  }
                  .ProseMirror pre {
                    background: #1e1e1e;
                    color: #d4d4d4;
                    padding: 12px 16px;
                    border-radius: 6px;
                    overflow-x: auto;
                    font-size: 11pt;
                  }
                  .ProseMirror table {
                    border-collapse: collapse;
                    width: 100%;
                    margin: 12px 0;
                  }
                  .ProseMirror td, .ProseMirror th {
                    border: 1px solid #d1d5db;
                    padding: 6px 10px;
                    min-width: 60px;
                    position: relative;
                  }
                  .ProseMirror th { background: #f9fafb; font-weight: 600; }
                  .ProseMirror .selectedCell { background: rgba(26,36,64,0.08); }
                  .ProseMirror img { max-width: 100%; height: auto; border-radius: 4px; }
                  .ProseMirror a { color: #2563eb; text-decoration: underline; }
                  .ProseMirror mark { border-radius: 2px; }
                  .ProseMirror p.is-editor-empty:first-child::before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                    float: left;
                    height: 0;
                  }
                `}</style>
                <EditorContent editor={editor} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Yeni Belge Modal ── */}
      {showYeniModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowYeniModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#111827' }}>Yeni Belge Oluştur</h4>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Belge Adı</label>
            <input
              autoFocus
              value={yeniBelgeAdi}
              onChange={e => setYeniBelgeAdi(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleYeniBelge(); }}
              placeholder="Belge adını girin..."
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowYeniModal(false)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                İptal
              </button>
              <button onClick={handleYeniBelge} disabled={yeniLoading}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#1a2440', color: '#fff', fontSize: 13, fontWeight: 600, cursor: yeniLoading ? 'not-allowed' : 'pointer', opacity: yeniLoading ? 0.7 : 1 }}>
                {yeniLoading ? 'Oluşturuluyor...' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Kopyala Modal ── */}
      {showKopyalaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowKopyalaModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#111827' }}>Belgeyi Kopyala</h4>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Yeni Belge Adı</label>
            <input
              autoFocus
              value={kopyaAdi}
              onChange={e => setKopyaAdi(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleKopyala(); }}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowKopyalaModal(false)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                İptal
              </button>
              <button onClick={handleKopyala}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#1a2440', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Kopyala
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Paylaş Modal ── */}
      {showPaylasModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowPaylasModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 500, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>Belgeyi Paylaş</h4>
                <button onClick={() => setShowPaylasModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}>×</button>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
                "{acikBelge?.belgeAdi}" belgesini diğer kullanıcılarla paylaşın.
              </p>
            </div>

            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <input
                value={paylasAra}
                onChange={e => setPaylasAra(e.target.value)}
                placeholder="Kullanıcı ara..."
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
              {filteredKullanicilar.map(k => {
                const secili = secilenPaylasimlar.find(p => p.kullaniciID === k.kullaniciID);
                return (
                  <div key={k.kullaniciID}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px', borderRadius: 8, cursor: 'pointer', marginBottom: 2 }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <input type="checkbox" checked={!!secili} onChange={() => togglePaylasim(k)}
                      style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0, accentColor: '#1a2440' }} />
                    <div style={{ flex: 1 }} onClick={() => togglePaylasim(k)}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{k.adSoyad}</div>
                      {k.email && <div style={{ fontSize: 11, color: '#9ca3af' }}>{k.email}</div>}
                    </div>
                    {secili && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={secili.duzenlemeYetkisi} onChange={() => toggleDuzenleme(k.kullaniciID)}
                          style={{ accentColor: '#cd6906', cursor: 'pointer' }} />
                        Düzenleme
                      </label>
                    )}
                  </div>
                );
              })}
              {filteredKullanicilar.length === 0 && (
                <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>Kullanıcı bulunamadı</div>
              )}
            </div>

            {secilenPaylasimlar.length > 0 && (
              <div style={{ padding: '8px 20px', borderTop: '1px solid #f0f0f0', background: '#f9fafb' }}>
                <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
                  {secilenPaylasimlar.length} kullanıcı seçili
                  {secilenPaylasimlar.filter(p => p.duzenlemeYetkisi).length > 0 &&
                    ` (${secilenPaylasimlar.filter(p => p.duzenlemeYetkisi).length} düzenleme yetkili)`}
                </p>
              </div>
            )}

            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowPaylasModal(false)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                İptal
              </button>
              <button onClick={handlePaylas} disabled={paylasLoading}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, cursor: paylasLoading ? 'not-allowed' : 'pointer', opacity: paylasLoading ? 0.7 : 1 }}>
                {paylasLoading ? 'Kaydediliyor...' : 'Paylaşımı Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetinEditoru;
